import { IconLoop } from '@developerz/ui';
import { useParams } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { Match, Show, Switch } from 'solid-js';
import { LinkTabs } from '../components/LinkTabs';
import { PageHeader } from '../components/PageHeader';
import { PolicyStatusBadge } from '../components/PolicyStatusBadge';
import { QueryBoundary } from '../components/QueryBoundary';
import { RepoAccessBlockers } from '../components/RepoAccessBlockers';
import { RepoAuditTab } from '../components/RepoAuditTab';
import { RepoIssuesTab } from '../components/RepoIssuesTab';
import { RepoKbTab } from '../components/RepoKbTab';
import { RepoOverviewTab } from '../components/RepoOverviewTab';
import { RepoPendingTab } from '../components/RepoPendingTab';
import { RepoPolicyTab } from '../components/RepoPolicyTab';
import { RepoReadiness } from '../components/RepoReadiness';
import { RepoReviewsTab } from '../components/RepoReviewsTab';
import { RepoScorecard } from '../components/RepoScorecard';
import { RepoSecretsGate } from '../components/RepoSecretsGate';
import { RepoSettingsTab } from '../components/RepoSettingsTab';
import { Skeleton } from '../components/Skeleton';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { useRepoReadiness } from '../lib/readiness';
import { safeDecode, useRepo, useRepoPendingPrs } from '../lib/repo-detail';
import { policyStatus } from '../lib/repos';
import { repoTabs } from '../lib/repoTabs';
import { roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';

// Per-repo workspace (#195). Container — resolves the route params, renders the
// identity header + tab bar, and mounts the active tab's component. Tabs map to
// subroutes per docs/idea/dashboard.md; each tab fetches its own slice. Which
// ones this caller gets is `lib/repoTabs.ts` (Secrets is owner-only, #1418).

/** `token` is injectable for tests; the router mounts this with no props → dev session. */
export function RepoDetail(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const me = useMe(token);
  const params = useParams<{ full_name: string; tab?: string }>();
  // Slashes in `owner/repo` are percent-encoded by the repos list before
  // navigation so the route matcher captures the whole pair. Decode for
  // display; keep the encoded form for tab hrefs so they round-trip the same.
  const encoded = (): string => params.full_name;
  const display = (): string => safeDecode(params.full_name);
  const tab = (): string => params.tab ?? '';

  const repo = useRepo(() => encoded(), token);
  // The Pending tab's badge reads the tab's own server-filtered list (open +
  // `ask`, plan 101 slice 17 row 17-2); a further page reads "N+".
  const prs = useRepoPendingPrs(() => encoded(), token);
  // The ladder is addressed by the plain `owner/repo`, not the route's encoded
  // param — `lib/readiness.ts` percent-encodes it as a query VALUE.
  const readiness = useRepoReadiness(display, token);
  const pendingCount = (): number => prs.rows().length;

  return (
    <>
      <PageHeader
        title={display()}
        desc={t('repo_detail.subtitle')}
        actions={
          <>
            <Show when={repo.data}>
              {(r) => (
                <>
                  {/* Read-only state signals — the operable toggle lives in Settings. */}
                  <StatusPill tone={r().enabled ? 'success' : 'neutral'}>
                    {t(r().enabled ? 'repo_detail.enabled' : 'repo_detail.disabled')}
                  </StatusPill>
                  <PolicyStatusBadge status={policyStatus(r())} />
                </>
              )}
            </Show>
            <Button variant="outline" size="sm" onClick={() => void repo.refetch()}>
              <IconLoop />
              {t('repo_detail.head_sync')}
            </Button>
          </>
        }
      />

      {/* Readiness (arc 101 slice 12 step 3). ONE predicate, two consumers: the
          `next_steps` MCP verb and this panel read the SAME ladder. What stood
          here was the second implementation — a banner re-deriving the
          `repo_onboarded` rung from `ai_first_at`, over its own catalog copy of
          `TASK_STALL_REMEDY`. The server's rung serves that sentence plus the
          refinement no browser can make ("a setup pass is already running"). */}
      <QueryBoundary query={readiness} loading={<Skeleton height="12rem" />}>
        {(ladder) => <RepoReadiness ladder={ladder} />}
      </QueryBoundary>

      {/* Onboarding (epic #3756): the open access asks on this repo, then the
          latest onboarding run's scorecard off the repo row. Each renders
          nothing when there is nothing to show. */}
      <RepoAccessBlockers fullName={display()} token={token} />
      <Show when={repo.data?.ai_first_score}>
        {(score) => <RepoScorecard score={score()} aiFirstAt={repo.data?.ai_first_at ?? null} />}
      </Show>

      <div class="tabs">
        {/* Link tabs, not role=tab — each tab is a subroute (`<LinkTabs/>`). */}
        <LinkTabs
          label={t('repo_detail.tabs_label')}
          items={repoTabs(me.data?.role, roleSettled(me)).map((item) => ({
            href: orgHref(`/repos/${encoded()}${item.href}`),
            label: t(item.label),
            ...(item.key === 'pending' && {
              trail: () => (
                <Show when={pendingCount() > 0}>
                  <span class="badge">
                    {prs.hasMore()
                      ? t('repo_detail.pending_count_more', { count: pendingCount() })
                      : pendingCount()}
                  </span>
                </Show>
              ),
            }),
          }))}
        />

        {/* Unknown sub-paths fall back to Overview rather than a dead 404 —
            the tab bar above stays the way out. */}
        <div class="tabs__panel">
          <Switch fallback={<RepoOverviewTab encoded={encoded()} token={token} />}>
            <Match when={tab() === 'issues'}>
              <RepoIssuesTab encoded={encoded()} token={token} />
            </Match>
            <Match when={tab() === 'policy'}>
              <RepoPolicyTab
                encoded={encoded()}
                token={token}
                policy_valid={repo.data?.policy_valid}
              />
            </Match>
            <Match when={tab() === 'pending'}>
              <RepoPendingTab encoded={encoded()} token={token} />
            </Match>
            <Match when={tab() === 'audit'}>
              <RepoAuditTab encoded={encoded()} token={token} />
            </Match>
            <Match when={tab() === 'reviews'}>
              <RepoReviewsTab encoded={encoded()} token={token} />
            </Match>
            {/* The KB routes address a repo by UUID, not `owner/repo`, so this
                waits on the repo row for its id — same as Secrets below. Through
                a QueryBoundary, never a bare Show: loading, failed and missing
                all painted a blank panel with no Retry (plan 101 slice 17 row
                17-13). */}
            <Match when={tab() === 'kb'}>
              <QueryBoundary query={repo} loading={<Skeleton height="10rem" />}>
                {(r) => <RepoKbTab repoId={r.id} token={token} />}
              </QueryBoundary>
            </Match>
            <Match when={tab() === 'settings'}>
              <RepoSettingsTab encoded={encoded()} token={token} />
            </Match>
            <Match when={tab() === 'secrets'}>
              <QueryBoundary query={repo} loading={<Skeleton height="10rem" />}>
                {(r) => <RepoSecretsGate fullName={r.full_name} token={token} />}
              </QueryBoundary>
            </Match>
          </Switch>
        </div>
      </div>
    </>
  );
}
